import React from 'react'
import * as state from '../../states'

import {
	IRole,
	TRoleAccess,
	ERoleAccess,
	ERoleAccessIsAny,
	ERoleExtraIsBuyer,
	ROLE_ACCESS_IS_ANY_LABEL_MAP
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconDeleteBin,
	IconEdit,
	UiMoreTextInfo,
	headCellFormatterSortable,
	ESortOrder,
	IconCopy,
	RoleExtraIsBuyerIcon,
	CheckBoxStatusIcon
} from '../ui'
import { ACCESS_IS_ANY_COLORS_MAP } from '../../constants'
import { useAtomValue } from 'jotai'
import { ROLE_PAGE_SORT_FIELDS, IRoleSortField, useMultiSelectTable } from '../../hooks'
import { Access, Rule } from '../../lib'
import { $rules } from './RoleAccessFields'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'


type IRoleListTableProps = {
	items: IRole[];
	skip: number;
	sortBy: IRoleSortField;
	setSortBy: (sortBy: IRoleSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onEdit?: (item: IRole, e: React.MouseEvent) => void;
	onClone?: (item: IRole, e: React.MouseEvent) => void;
	onRemove?: (item: IRole, e: React.MouseEvent) => void;
	onMultiSelect?: (selectedItems: IRole[]) => void;
}

type IRoleAccessProps = {
	value: Array<TRoleAccess>;
	rowData: IRole; // { }
	onClick?: (rowData: IRole, e: React.MouseEvent) => void;
	classNamesToAdd?: string;
}

const RoleAccessIsAnyField = (props: IRoleAccessProps) => {
	const { value, classNamesToAdd, rowData, onClick } = props
	const { t } = useTranslation()
	const valueCalc: ERoleAccessIsAny = value
		.find(item => item.$key === ERoleAccess.Any)
		?.$value === true ? ERoleAccessIsAny.Any : ERoleAccessIsAny.Custom
	const keyTrans = ROLE_ACCESS_IS_ANY_LABEL_MAP?.[valueCalc]
	const labelTrans = t(keyTrans)

	return <>
		<span
			/*style={{cursor: 'pointer'}}*/
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
			onClick={(e) => onClick && onClick(rowData, e)}
		>
			{labelTrans}
		</span>
	</>
}

export const RoleListTable = (props: IRoleListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onEdit,
		onClone,
		onRemove,
		onMultiSelect
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)
	const isMultiSelectEnabled = !!$access.$(ERoleAccess.RoleRemove)

	const { multiSelectConfig, selectedRowIndexes } = useMultiSelectTable(
		{
			items,
			isEnabled: isMultiSelectEnabled,
			onMultiSelect
		}
	)

	const roleListTableConfig: IUiTableConfig<IRole>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.role_list_table.column_id)
		},
		{
			key: 'code',
			title: t(ALL_LANG_KEY.role_list_table.column_code)
		},
		{
			key: 'name',
			title: t(ALL_LANG_KEY.role_list_table.column_name)
		},
		{
			key: 'extra.isBuyer',
			title: t(ALL_LANG_KEY.role_list_table.column_is_buyer),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ rowData }) => {
				const isBuyer = rowData.extra.isBuyer
				return <>
					<RoleExtraIsBuyerIcon
						value={isBuyer ? ERoleExtraIsBuyer.Buyer : isBuyer === false ? ERoleExtraIsBuyer.NotBuyer : null}/>
				</>
			}
		},
		{
			key: 'extra.inGroup',
			title: t(ALL_LANG_KEY.role_list_table.column_in_group),
			rowCellFormatter: ({ rowData }) => {
				const inGroup = rowData.extra.inGroup ?? null
				return <div style={{ textAlign: 'center' }}>
					{inGroup}
				</div>
			}
		},
		{
			key: 'extra.isTeam',
			title: t(ALL_LANG_KEY.role_list_table.column_is_team),
			rowCellFormatter: ({ rowData }) => {
				const isTeam = rowData.extra.isTeam ?? null
				return <div style={{ textAlign: 'center' }}>
					{isTeam}
				</div>
			}
		},
		{
			key: 'teamReport',
			title: t(ALL_LANG_KEY.role_list_table.column_team_report)
		},
		{
			key: 'Permissions',
			title: t(ALL_LANG_KEY.role_list_table.column_permissions),
			rowCellFormatter: ({ value, rowData }) => {
				const rules = rowData?.access ?? []
				const $rule = new Rule(rules)
				const items = $rules($rule, t)

				//console.log('Permissions: ', {value, rowData, itemsRules: items})
				const valueFormat: Array<string> = items?.filter(_ => !!_?.checked)?.map(_ => (_?.name as string) ?? '')

				return <UiMoreTextInfo value={valueFormat}/>
			}
		},
		{
			key: 'isSystem',
			title: t(ALL_LANG_KEY.role_list_table.column_is_system),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				//TODO: formatter for 'isSystem' field in edit mode (dropdown select...?)
				return <>
					<CheckBoxStatusIcon value={rowData.isSystem}
					                    iconCheckColor={'--dangerColor'}
					                    rowData={rowData}
					                    onClick={(rowData, e) => {
						                    console.log('Click on isSystem field :::', { value, rowData, rowIndex, cellIndex })
					                    }}
					/>
				</>
			}
		},
		/* {
			key: 'notes',
			title: 'Notes'
		}, */
		{
			key: 'access',
			title: t(ALL_LANG_KEY.role_list_table.column_access),
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const any: boolean = value.find((item: TRoleAccess) => item.$key === ERoleAccess.Any)?.$value === true
				const valueFormat: ERoleAccessIsAny = any ? ERoleAccessIsAny.Any : ERoleAccessIsAny.Custom

				return <div style={{ textAlign: 'center' }}
				>
					<RoleAccessIsAnyField
						value={value}
						rowData={rowData}
						classNamesToAdd={ACCESS_IS_ANY_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on access field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</div>
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				const isRemoveBtnActive = !rowData.isSystem

				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>
						{
							!!onEdit && !!$access.$(ERoleAccess.RoleModify)
								? (
									<button name="edit"
									        className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
									        onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}

						{
							!!onClone && !!$access.$(ERoleAccess.RoleModify)
								? (
									<button name="clone"
									        className="icon border-0 rounded-circle text-center bg-primary-transparent"
									        onClick={(e) => onClone && onClone(rowData, e)}
									>
										<IconCopy/>
									</button>
								)
								: null
						}

						{
							!!onRemove && !!$access.$(ERoleAccess.RoleRemove)
								? (
									<button
										name="delete"
										className="icon border-0 rounded-circle text-center trash bg-danger-transparent delete-item"
										onClick={(e) => onRemove && isRemoveBtnActive && onRemove(rowData, e)}
										disabled={!isRemoveBtnActive}
									>
										<IconDeleteBin/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const roleListTableConfigSortable: IUiTableConfig<IRole>[] = roleListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = ROLE_PAGE_SORT_FIELDS.includes(configItem.key as IRoleSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('RoleListTable:: ', { roleListTableConfig, roleListTableConfigSortable })
	const tableConfig = isMultiSelectEnabled ? [multiSelectConfig, ...roleListTableConfigSortable] : roleListTableConfigSortable

	return <>
		<UiTable
			data={items}
			config={tableConfig}
			selectedRowIndexes={selectedRowIndexes}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}